import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import sanitizeHtml from 'sanitize-html';
import I18n from 'i18n-js';
import Icon from '@/components/ui/Icon';
import Dropdown from '@/components/ui/Dropdown';
import DndAnswerContext from 'components/common/Context';
import cn from '@/utils/cn';
import Text from '../../../../ui/Text';

const DraggableChoiceContent = (props) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { answerChoiceDispatch, dropZones, hasReusableAnswerChoices, isComplete } = useContext(DndAnswerContext);
  const isShapeCircle = Object.values(dropZones || {})[0]?.shape === 'circle';

  // Configure sanitizeHTML to allow froala styles/images.
  const sanitize = (content) => {
    // wrap strings with superscripts|subscripts in a span to get them to display properly
    let dirty = /<sup>|<sub>/.test(content) ? `<span>${content}</span>` : content;
    // Add `draggable="false"` to img tags, so Safari doesn't give a ghost image
    dirty = content.replaceAll('<img ', '<img draggable="false" ');

    return {
      __html: sanitizeHtml(
        dirty, {
          allowedAttributes: {
            img: ['src', 'alt', 'data-snippet', 'style', 'draggable'],
            li: ['style'],
            p: ['style'],
            span: ['style'],
          },
          allowedClasses: {
            img: ['imageInQuestion', 'fr-fic', 'fr-dii', 'fr-draggable'],
          },
          allowedSchemes: ['data', 'http', 'https'],
          allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img']),
        }
      ),
    };
  };

  const focusNextDraggable = () => {
    const draggableArray = Array.from(document.querySelectorAll('[draggable=true]'));

    const currentIndex = draggableArray.findIndex(
      element => element.getAttribute('data-draggable-id') === props.draggableChoiceId
    );

    const nextIndex = (currentIndex + 1) % draggableArray.length;
    const nextDraggable = draggableArray[nextIndex];

    nextDraggable?.focus();
  };

  const moveDraggableToBank = () => {
    answerChoiceDispatch({
      payload: {
        draggableChoiceId: props.draggableChoiceId,
        hasReusableAnswerChoices,
        sourceDropZoneId: props.sourceDropzoneId || null,
      },
      type: 'REMOVE',
    });
  };

  const moveDraggableToDropZone = (key) => {
    answerChoiceDispatch({
      payload: {
        draggableChoiceId: props.draggableChoiceId,
        dropZoneId: key,
        hasReusableAnswerChoices,
        sourceDropZoneId: props.sourceDropzoneId || null,
      },
      type: 'ADD',
    });

    setDropdownOpen(false);
    focusNextDraggable();
  };

  const draggableContent = (
    <div
      className={`fr-view fr-element ${props.className}`}
      dangerouslySetInnerHTML={sanitize(props.content)}
      tabIndex={-1}
    />
  );

  if (!props.showAccessibilityStyling || props.isAnswerKeyView || isShapeCircle) {
    return draggableContent;
  }

  if (props.isDragged) {
    return (
      <div className="tw-px-5 tw-pt-[1px] tw-relative">
        {draggableContent}
        <button
          aria-label={I18n.t('return_item_to_choice_bank')}
          className={cn('tw-absolute tw--top-1.5 tw--right-1.5 tw-border-none tw-bg-white tw-rounded tw-p-0',
            'tw-w-[1.5rem] tw-h-[1.5rem] tw-flex tw-items-center tw-justify-center',
            'focus:tw-outline focus:tw-outline-2 focus:tw-outline-[#522e91] tw-z-9999',
            { 'tw-hidden': !props.isHoveredOrFocused || isComplete })
          }
          data-draggable-location="dropzone"
          tabIndex={0}
          type="button"
          onClick={moveDraggableToBank}
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') moveDraggableToBank();
          }}
        >
          <Icon color="#777770" icon="Close" size={12} />
        </button>
      </div>
    );
  }

  return (
    <div className="tw-px-6 tw-pt-[1px]">
      {isComplete ? (
        draggableContent
      ) : (
        <Dropdown open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <Dropdown.Anchor asChild>
            {draggableContent}
          </Dropdown.Anchor>
          <Dropdown.Trigger asChild>
            <button
              aria-label={I18n.t('move_to_drop_zone')}
              className={cn('tw-border-none tw-h-[24px] tw-w-[24px] tw-bg-white tw-rounded tw-absolute tw-top-1.5',
                'tw-right-[2px] tw-p-0 tw-pt-[2px] focus:tw-outline focus:tw-outline-2 focus:tw-outline-[#522e91]',
                { 'tw-hidden': !props.isHoveredOrFocused })
              }
              tabIndex={0}
              type="button"
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  setDropdownOpen(true);
                }
              }}
            >
              <Icon className="tw-bg-transparent" color="#3c3c3c" icon="MoreMenuVertical" size={16} />
            </button>
          </Dropdown.Trigger>
          <Dropdown.Content>
            <Text className="tw-flex tw-justify-center tw-p-1" size="sm" tag="h4">
              {I18n.t('move_to_drop_zone')}
            </Text>
            {Object.entries(dropZones).map(([key, dropZone]) => (
              <Dropdown.Button
                key={key}
                className="tw-px-2 focus:tw-outline focus:tw-outline-2 focus:tw-outline-[#522e91]"
                onClick={() => moveDraggableToDropZone(key)}
                onSelect={() => setDropdownOpen(false)}
              >
                <Text className="!tw-text-tci-black" size="sm">{dropZone.title || key.toUpperCase()}</Text>
              </Dropdown.Button>
            ))}
          </Dropdown.Content>
        </Dropdown>
      )}
    </div>
  );
};

DraggableChoiceContent.defaultProps = {
  className: '',
  content: '',
  draggableChoiceId: null,
  isAnswerKeyView: false,
  isDragged: false,
  isHoveredOrFocused: false,
  showAccessibilityStyling: false,
  sourceDropzoneId: null,
};

DraggableChoiceContent.propTypes = {
  className: PropTypes.string,
  content: PropTypes.string,
  draggableChoiceId: PropTypes.string,
  isAnswerKeyView: PropTypes.bool,
  isDragged: PropTypes.bool,
  isHoveredOrFocused: PropTypes.bool,
  showAccessibilityStyling: PropTypes.bool,
  sourceDropzoneId: PropTypes.any,
};

export default DraggableChoiceContent;
